import { apiFetch } from '../client';
import type { ListItem } from '../SharePointListClient';

/**
 * AR Puntos de Venta — ARCA emisor identities. Backs the Configuración CRUD
 * and the Punto de Venta selector in the Facturar dialog. Plain REST
 * (`/api/puntos-venta`), not the SharePoint-list framework, because the
 * underlying `puntos_venta` table is Supabase-native. Rows are shaped as
 * `ListItem` so the Configuración entity + CrudTable consume them uniformly.
 */
export type PuntosVentaFields = {
  RazonSocial?: string | null;
  NroPuntoVenta?: number | null;
  Domicilio?: string | null;
  IngresosBrutos?: string | null;
  InicioActividades?: string | null;
  CondicionIva?: string | null;
  /** FCE MiPyME: emisor CBU (Opcional 2101). */
  Cbu?: string | null;
  /** FCE MiPyME: transmisión (Opcional 27): 'ADC' | 'SCA'. */
  FceTransmision?: string | null;
  /** PV de exportación (WSFEX, Factura E). Los A/B/C usan los PV con false. */
  EsExportacion?: boolean;
  /** PV de exportación: diferencia máxima (%) entre la cotización declarada y la de ARCA. */
  FexDesvioMaxPct?: number;
  Status?: 'Activo' | 'Inactivo' | string | null;
};

/** Default del desvío máximo de cotización (mismo que la columna y el server). */
export const FEX_DESVIO_MAX_PCT_DEFAULT = 10;

export type PuntoVentaItem = ListItem<PuntosVentaFields>;

type PvRow = {
  id: number;
  razon_social: string | null;
  nro_punto_venta: number | null;
  domicilio: string | null;
  ingresos_brutos: string | null;
  inicio_actividades: string | null;
  condicion_iva: string | null;
  cbu: string | null;
  fce_transmision: string | null;
  es_exportacion?: boolean | null;
  fex_desvio_cotizacion_max_pct?: number | string | null;
  status: string | null;
  created_at: string | null;
  updated_at: string | null;
};

const toItem = (r: PvRow): PuntoVentaItem => ({
  id: String(r.id),
  createdDateTime: r.created_at ?? '',
  lastModifiedDateTime: r.updated_at ?? '',
  fields: {
    RazonSocial: r.razon_social,
    NroPuntoVenta: r.nro_punto_venta,
    Domicilio: r.domicilio,
    IngresosBrutos: r.ingresos_brutos,
    InicioActividades: r.inicio_actividades,
    CondicionIva: r.condicion_iva,
    Cbu: r.cbu,
    FceTransmision: r.fce_transmision,
    EsExportacion: r.es_exportacion === true,
    FexDesvioMaxPct: Number(r.fex_desvio_cotizacion_max_pct) || FEX_DESVIO_MAX_PCT_DEFAULT,
    Status: r.status,
  },
});

/** All Puntos de Venta (active + inactive). Drives the Configuración CRUD. */
export async function listAllPuntosVenta(): Promise<PuntoVentaItem[]> {
  const res = await apiFetch<{ items: PvRow[] }>('/api/puntos-venta');
  return res.items.map(toItem);
}

/** Active only — drives the Facturar dialog's Punto de Venta selector. */
export async function listActivePuntosVenta(): Promise<PuntoVentaItem[]> {
  const items = await listAllPuntosVenta();
  return items.filter((p) => p.fields.Status === 'Activo');
}

export type PuntoVentaPayload = {
  razonSocial: string;
  nroPuntoVenta: number | null;
  domicilio: string | null;
  ingresosBrutos: string | null;
  inicioActividades: string | null;
  condicionIva: string | null;
  cbu: string | null;
  fceTransmision: 'ADC' | 'SCA' | null;
  esExportacion: boolean;
  fexDesvioMaxPct: number;
  status?: 'Activo' | 'Inactivo';
};

const toBody = (p: PuntoVentaPayload) => ({
  razon_social: p.razonSocial,
  nro_punto_venta: p.nroPuntoVenta,
  domicilio: p.domicilio,
  ingresos_brutos: p.ingresosBrutos,
  inicio_actividades: p.inicioActividades,
  condicion_iva: p.condicionIva,
  cbu: p.cbu,
  fce_transmision: p.fceTransmision,
  es_exportacion: p.esExportacion,
  fex_desvio_cotizacion_max_pct: p.fexDesvioMaxPct,
  ...(p.status ? { status: p.status } : {}),
});

export async function createPuntoVenta(p: PuntoVentaPayload): Promise<PuntoVentaItem> {
  const res = await apiFetch<{ item: PvRow }>('/api/puntos-venta', {
    method: 'POST',
    body: toBody(p),
  });
  return toItem(res.item);
}

export async function updatePuntoVenta(
  id: string,
  p: PuntoVentaPayload,
): Promise<PuntoVentaItem> {
  const res = await apiFetch<{ item: PvRow }>(`/api/puntos-venta/${id}`, {
    method: 'PATCH',
    body: toBody(p),
  });
  return toItem(res.item);
}

export async function softDeletePuntoVenta(id: string): Promise<PuntoVentaItem> {
  const res = await apiFetch<{ item: PvRow }>(`/api/puntos-venta/${id}`, {
    method: 'DELETE',
  });
  return toItem(res.item);
}
